import { cs } from "../utils/currency";
import React, { useEffect, useState } from "react";
import { X, RotateCcw, Undo2 } from "lucide-react";
import toast from "react-hot-toast";
import api, { cancelOrder, processOrderRefunds } from "../services/api";

/** Minimal order shape both modals need. */
export interface ActionOrder {
  _id: string;
  orderNumber: string;
  totalAmount: number;
  phoneNumber?: string;
  payChoice?: string;
  orderStatus?: string;
}

const PAYMENT_LABELS: Record<string, string> = {
  card: "Card",
  stripe: "Card",
  square: "Card",
  wallet: "Wallet",
  bonus_wallet: "Bonus Wallet",
  cash: "Cash",
};

const ModalShell: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 className="font-bold text-gray-900 m-0">{title}</h2>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg" aria-label="Close">
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>
      <div className="p-6 space-y-4">{children}</div>
    </div>
  </div>
);

// ─── Refund ───────────────────────────────────────────────────────────────────

/**
 * Full refund: cancels the order (refund entries + ledger + loyalty reversal on
 * the server), then processes the refunds so card payments go back through
 * Square/Stripe and wallet payments are credited back.
 */
export const RefundOrderModal: React.FC<{
  order: ActionOrder;
  onClose: () => void;
  onDone: (orderId: string) => void;
}> = ({ order, onClose, onDone }) => {
  const [saving, setSaving] = useState(false);
  const method = PAYMENT_LABELS[order.payChoice || ""] || order.payChoice || "original payment";

  const handleRefund = async () => {
    try {
      setSaving(true);
      const res = await cancelOrder(order._id);
      if (!res.data?.success) throw new Error(res.data?.error || "Refund failed");
      if (res.data.refundError) {
        toast.error(`Order cancelled, but the refund could not be recorded: ${res.data.refundError}`);
      } else {
        try {
          await processOrderRefunds(order._id);
          toast.success(`Order #${order.orderNumber} refunded to ${method}`);
        } catch {
          toast.error("Order cancelled — refund still pending. Finish it from the Refunds page.");
        }
      }
      onDone(order._id);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || "Failed to refund order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={`Refund Order #${order.orderNumber}`} onClose={onClose}>
      <div className="rounded-lg bg-gray-50 p-4 text-sm space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-500">Refund amount</span>
          <span className="font-semibold text-gray-900">{cs()}{order.totalAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Back to</span>
          <span className="font-medium text-gray-900">{method}</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 m-0">
        The order is cancelled and the full amount goes back to the original payment method (card refunds go
        through Square/Stripe). Loyalty earned on this order is taken back and the ledger is updated. This can't
        be undone.
      </p>
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleRefund}
          disabled={saving}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Undo2 className="w-4 h-4" />
          {saving ? "Refunding..." : `Refund $${order.totalAmount.toFixed(2)}`}
        </button>
      </div>
    </ModalShell>
  );
};

// ─── Compensate ───────────────────────────────────────────────────────────────

export type CompensationPayer = "restaurant" | "admin";

/** Who bears a compensation: the restaurant (owed in its ledger) or the admin/platform. */
export const CompensationPayerToggle: React.FC<{
  value: CompensationPayer;
  onChange: (value: CompensationPayer) => void;
}> = ({ value, onChange }) => (
  <div>
    <label className="block mb-1.5 text-xs font-medium text-gray-700">Paid by</label>
    <div className="flex gap-2">
      {(["restaurant", "admin"] as const).map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={`flex-1 py-1.5 rounded-md text-sm font-medium border transition ${
            value === option
              ? "bg-purple-600 text-white border-purple-600"
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          {option === "restaurant" ? "Restaurant" : "Admin"}
        </button>
      ))}
    </div>
    <p className="mt-1 text-xs text-gray-500 m-0">
      {value === "restaurant"
        ? "Added to the restaurant's ledger as owed to the platform."
        : "The platform absorbs it — no charge to the restaurant."}
    </p>
  </div>
);

type LoyaltyInfo = {
  loyaltyEarned?: number;
  loyaltyReversible?: boolean;
  compensationHistory?: { points: number }[];
  /** Also true when the compensation itself is hidden (platform-paid, admin-only) */
  alreadyCompensated?: boolean;
};

/** (compensation ÷ order total) × loyalty earned — same formula as the server. */
const loyaltyToRemove = (points: number, total: number, info: LoyaltyInfo | null) => {
  const earned = info?.loyaltyEarned ?? 0;
  if (!info?.loyaltyReversible || !(points > 0) || !(total > 0) || earned <= 0) return 0;
  return Math.min(earned, Math.round((points / total) * earned * 100) / 100);
};

export const CompensateOrderModal: React.FC<{
  order: ActionOrder;
  onClose: () => void;
  onDone: (orderId: string) => void;
  /** Location admins: no payer choice — the restaurant pays */
  restaurantPaysOnly?: boolean;
}> = ({ order, onClose, onDone, restaurantPaysOnly = false }) => {
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [paidBy, setPaidBy] = useState<CompensationPayer>("restaurant");
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState<LoyaltyInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);

  // Loyalty earned, payment and earlier compensation for this order
  useEffect(() => {
    if (!order.phoneNumber) {
      setLoadingInfo(false);
      return;
    }
    api
      .get(`/loyalty/orders/${encodeURIComponent(order.phoneNumber)}`, {
        params: { search: order.orderNumber },
      })
      .then((res) => {
        const match = (res.data?.data || []).find((o: any) => o._id === order._id);
        setInfo(match || {});
      })
      .catch(() => setInfo({}))
      .finally(() => setLoadingInfo(false));
  }, [order._id, order.orderNumber, order.phoneNumber]);

  const pts = parseFloat(points);
  const exceedsMax = !isNaN(pts) && pts > order.totalAmount;
  const alreadyCompensated = info?.alreadyCompensated ?? (info?.compensationHistory?.length ?? 0) > 0;
  const removeAmt = loyaltyToRemove(pts, order.totalAmount, info);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isNaN(pts) || pts <= 0) {
      toast.error("Enter a valid points amount.");
      return;
    }
    if (exceedsMax) {
      toast.error(`Points cannot exceed order total ($${order.totalAmount.toFixed(2)}).`);
      return;
    }
    try {
      setSaving(true);
      const res = await api.post("/loyalty/order-refund", {
        phoneNumber: order.phoneNumber,
        orderId: order._id,
        points: pts,
        reason,
        paidBy,
      });
      const removed = Number(res.data?.loyaltyRemoved) || 0;
      toast.success(
        removed > 0
          ? `Compensation added. ${removed.toFixed(2)} loyalty points from this order removed.`
          : "Compensation points added!",
      );
      onDone(order._id);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to process compensation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={`Compensate Order #${order.orderNumber}`} onClose={onClose}>
      {!order.phoneNumber ? (
        <p className="text-sm text-gray-600 m-0">
          This order has no customer phone number, so compensation points can't be added.
        </p>
      ) : loadingInfo ? (
        <p className="text-sm text-gray-400 m-0">Loading order details…</p>
      ) : alreadyCompensated ? (
        <p className="rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-700 m-0">
          This order has already been compensated.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
            <span>
              Customer: <span className="font-medium text-gray-800">{order.phoneNumber}</span>
            </span>
            <span>
              Loyalty earned:{" "}
              <span className="font-medium text-gray-800">{(info?.loyaltyEarned ?? 0).toFixed(2)} pts</span>
            </span>
          </div>
          <div>
            <label className="block mb-1.5 text-xs font-medium text-gray-700">
              Points <span className="text-gray-400 font-normal">(max ${order.totalAmount.toFixed(2)})</span>
            </label>
            <input
              type="number"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              placeholder="0.00"
              required
              step="0.01"
              min="0.01"
              max={order.totalAmount}
              className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${
                exceedsMax ? "border-red-400 focus:ring-red-400 bg-red-50" : "border-gray-300 focus:ring-blue-500"
              }`}
            />
          </div>
          <div>
            <label className="block mb-1.5 text-xs font-medium text-gray-700">Reason *</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Wrong order..."
              required
              className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {!restaurantPaysOnly && <CompensationPayerToggle value={paidBy} onChange={setPaidBy} />}
          {(info?.loyaltyEarned ?? 0) > 0 &&
            (info?.loyaltyReversible ? (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 m-0">
                Loyalty to remove: <span className="font-semibold">{removeAmt.toFixed(2)} pts</span>{" "}
                <span className="text-amber-700">
                  = ({isNaN(pts) ? "0.00" : pts.toFixed(2)} ÷ {order.totalAmount.toFixed(2)}) ×{" "}
                  {(info?.loyaltyEarned ?? 0).toFixed(2)}
                </span>
              </p>
            ) : (
              <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600 m-0">
                Paid in cash — loyalty points from this order are kept.
              </p>
            ))}
          <button
            type="submit"
            disabled={saving || exceedsMax}
            className="w-full flex items-center justify-center gap-2 py-2 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 transition disabled:opacity-60"
          >
            <RotateCcw size={14} />
            {saving ? "Processing..." : "Add Compensation"}
          </button>
        </form>
      )}
    </ModalShell>
  );
};
